<script>
  import Modeler from "bpmn-js/lib/Modeler.js";
  import "bpmn-js/dist/assets/bpmn-js.css";
  import "bpmn-js/dist/assets/diagram-js.css";
  import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
  import { Loading } from "carbon-components-svelte";
  import { mirrorOrientation } from "lib/util/bpmnOrientation.js";

  export let bpmnXml = "";
  export let loading = false;
  /** Was gerade laeuft - erscheint mittig ueber der Vorschau. */
  export let loadingText = "Generating BPMN Export";
  export let error = "";
  export let orientation = "horizontal";

  const dispatch = createEventDispatcher();
  const emptyDiagram = (name = "New Process") => `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_Draw" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_Draw" name="${escapeXml(name)}" isExecutable="false" />
  <bpmndi:BPMNDiagram id="BPMNDiagram_Draw"><bpmndi:BPMNPlane id="BPMNPlane_Draw" bpmnElement="Process_Draw" /></bpmndi:BPMNDiagram>
</bpmn:definitions>`;

  let viewdiv;
  let modeler;
  let loadedXml = "";
  let appliedOrientation = "horizontal";
  let orientationPending = false;
  let importError = "";
  let resizeObserver;
  let importing = false;
  let processStarted = false;
  let managedInformationIds = [];

  function escapeXml(value) {
    return String(value || "").replace(/[<>&"']/g, (character) => ({
      "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;",
    })[character]);
  }

  async function fitDiagram() {
    if (!modeler) return;
    await tick();
    requestAnimationFrame(() => {
      // Der Rahmen kann inzwischen weg sein: die Vorschau meldet sich kurz
      // verzoegert, und wer den Dialog vorher schliesst, bekam sonst einen
      // Fehler auf clientWidth eines nicht mehr vorhandenen Elements.
      if (!modeler || !viewdiv?.isConnected) return;
      const canvas = modeler.get("canvas");
      canvas.resized();
      canvas.zoom("fit-viewport");
    });
  }

  async function loadXml(xml) {
    if (!modeler || !xml || xml === loadedXml) return;
    try {
      importing = true;
      await modeler.importXML(xml);
      loadedXml = xml;
      processStarted = modeler.get("elementRegistry").filter((element) => element.type === "bpmn:StartEvent").length > 0;
      importError = "";
      fitDiagram();
    } catch (err) {
      importError = err.message || "Failed to open BPMN model.";
      console.error("Failed to render editable BPMN diagram:", err);
    } finally {
      importing = false;
      emitState();
    }
  }

  /**
   * Dreht den gezeichneten Stand. Bewusst aus dem Modeler heraus, damit alles
   * erhalten bleibt, was der Nutzer bereits gezeichnet oder verschoben hat.
   */
  async function applyOrientation(target) {
    if (!modeler || !loadedXml || orientationPending || target === appliedOrientation) return;
    orientationPending = true;
    try {
      const result = await modeler.saveXML({ format: true });
      const mirrored = mirrorOrientation(result?.xml || "");
      await modeler.importXML(mirrored);
      loadedXml = mirrored;
      appliedOrientation = target;
      fitDiagram();
    } catch (err) {
      console.error("Failed to change BPMN orientation:", err);
    } finally {
      orientationPending = false;
      emitState();
    }
  }

  $: if (modeler && bpmnXml) loadXml(bpmnXml);
  $: if (modeler && loadedXml) applyOrientation(orientation);

  export async function saveXML() {
    if (!modeler || !processStarted) return "";
    const result = await modeler.saveXML({ format: true });
    return result?.xml || "";
  }

  export async function replaceXML(xml) {
    loadedXml = "";
    // Das Backend layoutet horizontal. Nach dem Ersetzen muss die gewaehlte
    // Ausrichtung deshalb erneut angewandt werden, sonst steht das Diagramm
    // wieder quer, obwohl vertikal gewaehlt ist.
    appliedOrientation = "horizontal";
    await loadXml(xml);
    await applyOrientation(orientation);
  }

  /** Aktueller Stand, immer horizontal - so erwartet ihn das Backend-Layout. */
  export async function saveHorizontalXML() {
    if (!modeler || !processStarted) return "";
    const result = await modeler.saveXML({ format: true });
    const xml = result?.xml || "";
    return appliedOrientation === "vertical" ? mirrorOrientation(xml) : xml;
  }

  function services() {
    return {
      canvas: modeler.get("canvas"),
      elementFactory: modeler.get("elementFactory"),
      elementRegistry: modeler.get("elementRegistry"),
      modeling: modeler.get("modeling"),
    };
  }

  function createShape(type, name, position, parent) {
    const { elementFactory, modeling } = services();
    const shape = elementFactory.createShape({ type });
    modeling.createShape(shape, position, parent);
    if (name) modeling.updateProperties(shape, { name });
    return shape;
  }

  // Die Zwischenereignisse gehoeren dazu: die Durchlaufzeiten setzen Timer
  // zwischen die Aktivitaeten. Fehlten sie hier, waehlte insertionContext den
  // falschen Anker und ein neues Gateway landete genau auf so einem Timer.
  const FLOW_NODE_TYPES = [
    "bpmn:StartEvent", "bpmn:Task", "bpmn:SubProcess",
    "bpmn:ExclusiveGateway", "bpmn:ParallelGateway", "bpmn:InclusiveGateway",
    "bpmn:IntermediateCatchEvent", "bpmn:IntermediateThrowEvent", "bpmn:BoundaryEvent",
    "bpmn:EndEvent",
  ];

  function flowNodes() {
    return services().elementRegistry.filter((element) =>
      element.parent && FLOW_NODE_TYPES.includes(element.type)
    );
  }

  function insertionContext() {
    const { elementRegistry, modeling } = services();
    const end = elementRegistry.filter((element) => element.type === "bpmn:EndEvent")[0];
    const candidates = flowNodes().filter((element) => element.type !== "bpmn:EndEvent");
    const anchor = candidates.sort((a, b) => (b.x + b.width) - (a.x + a.width))[0];
    if (end && anchor) {
      elementRegistry.filter((element) => element.type === "bpmn:SequenceFlow" && element.source === anchor && element.target === end)
        .forEach((connection) => modeling.removeConnection(connection));
    }
    return { anchor, end };
  }

  function connect(source, target) {
    if (!source || !target) return null;
    const { modeling } = services();
    const connection = modeling.connect(source, target, { type: "bpmn:SequenceFlow" });
    if (source === target || source.x > target.x) {
      const channelY = Math.max(...flowNodes().map((element) => element.y + element.height), source.y + source.height) + 60;
      modeling.updateWaypoints(connection, [
        { x: source.x + source.width / 2, y: source.y + source.height },
        { x: source.x + source.width / 2, y: channelY },
        { x: target.x + target.width / 2, y: channelY },
        { x: target.x + target.width / 2, y: target.y + target.height },
      ]);
    }
    return connection;
  }

  /**
   * Rueckt ein frisch erzeugtes Artefakt auf den naechsten freien Platz.
   *
   * Notizen und Datenspeicher wurden bisher an eine feste Stelle gesetzt -
   * die Notiz 55 Einheiten rechts vom Task, die Artefakte in ein Raster oben.
   * Steht dort schon etwas, lagen sie uebereinander; in der Vorschau war das
   * sichtbar, weil dort kein Backend-Layout hinterherraeumt.
   *
   * Gesucht wird ringfoermig um den Wunschplatz, damit das Artefakt bei
   * seiner Aktivitaet bleibt. Die Beschriftung wandert mit.
   */
  function placeClear(element, gap = 14) {
    if (!element) return;
    const { elementRegistry, modeling } = services();
    const others = elementRegistry.filter((other) =>
      other !== element && other.parent && !other.waypoints && !other.labelTarget
      && !["bpmn:Participant", "bpmn:Lane"].includes(other.type)
    );
    const hits = (dx, dy) => others.some((other) =>
      element.x + dx < other.x + other.width + gap
      && other.x < element.x + dx + element.width + gap
      && element.y + dy < other.y + other.height + gap
      && other.y < element.y + dy + element.height + gap
    );
    if (!hits(0, 0)) return;
    const stepY = element.height + gap;
    const stepX = element.width + gap;
    for (let ring = 1; ring <= 10; ring += 1) {
      for (const [dx, dy] of [[0, -ring * stepY], [0, ring * stepY], [ring * stepX, 0], [-ring * stepX, 0]]) {
        if (hits(dx, dy)) continue;
        modeling.moveElements(element.label ? [element, element.label] : [element], { x: dx, y: dy });
        return;
      }
    }
  }

  /** Laeuft ein waagerechtes oder senkrechtes Stueck mitten durch das Rechteck? */
  function segmentCrossesNode(from, to, node, pad = 4) {
    const left = node.x + pad;
    const right = node.x + node.width - pad;
    const top = node.y + pad;
    const bottom = node.y + node.height - pad;
    if (Math.abs(from.y - to.y) < 3) {
      return from.y > top && from.y < bottom
        && Math.min(from.x, to.x) < right && Math.max(from.x, to.x) > left;
    }
    if (Math.abs(from.x - to.x) < 3) {
      return from.x > left && from.x < right
        && Math.min(from.y, to.y) < bottom && Math.max(from.y, to.y) > top;
    }
    return false;
  }

  /**
   * Raeumt nach jedem Einfuegen auf: Pool und Bahnen um den Inhalt legen und
   * Verbindungen umleiten, die durch einen fremden Knoten laufen.
   *
   * Beides faellt erst beim Weiterbauen auf. Der Pool wird einmal mit fester
   * Groesse angelegt (1500x440) und waechst sonst nie mit - eine spaeter
   * ergaenzte Aktivitaet stand dann rechts daneben (gemessen: Endereignis 67
   * Einheiten ausserhalb). Und eine bereits bestehende Verbindung bleibt
   * liegen, wenn ein neuer Knoten in ihre Bahn geraet; sie lief dann quer
   * durch den Kasten, statt aussen herum.
   */
  function tidyDiagram() {
    const { elementRegistry, modeling } = services();
    const nodes = flowNodes();
    if (!nodes.length) return;

    const participant = elementRegistry.filter((element) => element.type === "bpmn:Participant")[0];
    if (participant) {
      const padding = 70;
      // Fuer die Groesse zaehlt alles Sichtbare, nicht nur die Flussknoten:
      // Notizen und Datenspeicher standen sonst unten aus dem Pool heraus.
      const inside = elementRegistry.filter((element) =>
        element.parent && !element.waypoints && !element.labelTarget
        && !["bpmn:Participant", "bpmn:Lane"].includes(element.type)
      );
      const requiredRight = Math.max(...inside.map((node) => node.x + node.width)) + padding;
      const requiredBottom = Math.max(...inside.map((node) => node.y + node.height)) + padding;
      const lanes = elementRegistry
        .filter((element) => element.type === "bpmn:Lane")
        .sort((a, b) => a.y - b.y);

      if (lanes.length) {
        // Ueber die Bahnen gehen, nicht ueber den Pool: in BPMN spannen die
        // Bahnen den Pool auf, er waechst mit ihnen. Andersherum bliebe rechts
        // ein leerer Block hinter einer senkrechten Linie stehen - den kennt
        // eine Swimlane nicht.
        lanes.forEach((lane, index) => {
          const width = Math.max(lane.width, requiredRight - lane.x);
          // Nur die unterste Bahn waechst nach unten, sonst verschoebe sich
          // die Grenze zwischen zwei Bahnen.
          const height = index === lanes.length - 1
            ? Math.max(lane.height, requiredBottom - lane.y)
            : lane.height;
          if (width - lane.width > 0.5 || height - lane.height > 0.5) {
            modeling.resizeLane(lane, { x: lane.x, y: lane.y, width, height });
          }
        });
      } else if (requiredRight - participant.x > participant.width
        || requiredBottom - participant.y > participant.height) {
        modeling.resizeShape(participant, {
          x: participant.x,
          y: participant.y,
          width: Math.max(participant.width, requiredRight - participant.x),
          height: Math.max(participant.height, requiredBottom - participant.y),
        });
      }
    }

    const channelY = Math.max(...nodes.map((node) => node.y + node.height)) + 60;
    for (const connection of elementRegistry.filter((element) => element.type === "bpmn:SequenceFlow")) {
      const waypoints = connection.waypoints || [];
      if (waypoints.length < 2) continue;
      const blocking = nodes.find((node) =>
        node !== connection.source && node !== connection.target
        && waypoints.slice(0, -1).some((point, index) => segmentCrossesNode(point, waypoints[index + 1], node))
      );
      if (!blocking) continue;
      const source = connection.source;
      const target = connection.target;
      if (!source || !target) continue;
      // Unter dem Diagramm herum - dieselbe Spur, die auch Rueckwege nutzen.
      modeling.updateWaypoints(connection, [
        { x: source.x + source.width / 2, y: source.y + source.height },
        { x: source.x + source.width / 2, y: channelY },
        { x: target.x + target.width / 2, y: channelY },
        { x: target.x + target.width / 2, y: target.y + target.height },
      ]);
    }
  }

  function existingActivity(task) {
    const name = String(task?.name || task || "").trim().toLowerCase();
    if (!name) return null;
    return services().elementRegistry.filter((element) =>
      ["bpmn:Task", "bpmn:SubProcess"].includes(element.type)
      && String(element.businessObject?.name || "").trim().toLowerCase() === name
    )[0] || null;
  }

  function teamName(task) {
    return task?.teams?.[0] || "Other Teams";
  }

  function ensureTeamLane(name) {
    const { canvas, elementFactory, elementRegistry, modeling } = services();
    let participant = elementRegistry.filter((element) => element.type === "bpmn:Participant")[0];
    if (!participant) {
      const processRoot = canvas.getRootElement();
      participant = elementFactory.createParticipantShape();
      modeling.createShape(participant, { x: 850, y: 300 }, processRoot);
      modeling.resizeShape(participant, { x: 100, y: 20, width: 1500, height: 440 });
    }

    let lanes = elementRegistry.filter((element) => element.type === "bpmn:Lane" && element.parent === participant);
    let lane = lanes.find((element) => element.businessObject?.name === name);
    if (lane) return lane;

    if (!lanes.length) {
      lane = modeling.createShape({ type: "bpmn:Lane", isHorizontal: true }, {
        x: participant.x + 30,
        y: participant.y,
        width: participant.width - 30,
        height: participant.height,
      }, participant);
      participant.children.filter((element) => element !== lane && flowNodes().includes(element))
        .forEach((element) => {
          const laneCenterY = lane.y + lane.height / 2;
          modeling.moveShape(element, { x: 0, y: laneCenterY - (element.y + element.height / 2) }, lane);
        });
    } else {
      lanes = lanes.sort((a, b) => a.y - b.y);
      lane = modeling.addLane(lanes[lanes.length - 1], "bottom");
      modeling.resizeLane(lane, { x: lane.x, y: lane.y, width: lane.width, height: 360 });
    }
    modeling.updateProperties(lane, { name });
    return lane;
  }

  function moveEndAfter(end, element) {
    if (!end || !element) return;
    const { modeling } = services();
    const requiredX = element.x + element.width + 130;
    if (end.x < requiredX) {
      // Die Beschriftung mitnehmen: sie ist ein eigenes Element, und
      // moveShape allein laesst sie stehen - "End" hing dann weit links
      // neben dem Kreis in der Luft (gemessen: 161 Einheiten daneben).
      const delta = { x: requiredX - end.x, y: 0 };
      const moving = end.label ? [end, end.label] : [end];
      modeling.moveElements(moving, delta);
    }
    connect(element, end);
  }

  export async function initializeProcess(name) {
    await loadXml(emptyDiagram(name));
    const { canvas } = services();
    createShape("bpmn:StartEvent", "Start", { x: 180, y: 300 }, canvas.getRootElement());
    processStarted = true;
    fitDiagram();
    emitState();
  }

  export async function resetDiagram() {
    managedInformationIds = [];
    processStarted = false;
    await loadXml(emptyDiagram());
  }

  export function addActivities(tasks = [], mode = "sequence", options = {}) {
    if (!modeler || !processStarted || !tasks.length) return;
    const { modeling } = services();
    let { anchor, end } = insertionContext();
    const taskLanes = new Map(tasks.map((task) => [task.id || task.name, ensureTeamLane(teamName(task))]));

    if (mode === "parallel" || mode === "conditional") {
      const gatewayType = mode === "parallel" ? "bpmn:ParallelGateway" : "bpmn:ExclusiveGateway";
      const split = createShape(gatewayType, "", {
        x: (anchor?.x || 180) + (anchor?.width || 36) + 100,
        y: anchor.y + anchor.height / 2,
      }, anchor.parent);
      if (options.question) modeling.updateProperties(split, { name: options.question });
      connect(anchor, split);
      const branchX = split.x + split.width + 145;
      const laneIndexes = new Map();
      const laneCounts = tasks.reduce((counts, task) => {
        const lane = taskLanes.get(task.id || task.name);
        counts.set(lane.id, (counts.get(lane.id) || 0) + 1);
        return counts;
      }, new Map());
      const branchShapes = tasks.map((task) => {
        const existing = existingActivity(task);
        if (existing) return existing;
        const lane = taskLanes.get(task.id || task.name);
        const laneIndex = laneIndexes.get(lane.id) || 0;
        const laneCount = laneCounts.get(lane.id) || 1;
        laneIndexes.set(lane.id, laneIndex + 1);
        const spacing = laneCount > 1 ? Math.min(110, (lane.height - 100) / (laneCount - 1)) : 0;
        return createShape("bpmn:Task", task.name || String(task), {
          x: branchX,
          y: lane.y + lane.height / 2 + (laneIndex - (laneCount - 1) / 2) * spacing,
        }, lane);
      });
      const merge = createShape(gatewayType, "", {
        x: branchX + 170,
        y: split.y + split.height / 2,
      }, split.parent);
      branchShapes.forEach((shape, index) => {
        const outgoing = connect(split, shape);
        if (mode === "conditional") modeling.updateProperties(outgoing, { name: options.conditions?.[index] || (index === tasks.length - 1 ? "Else" : `Condition ${index + 1}`) });
        connect(shape, merge);
      });
      moveEndAfter(end, merge);
      tidyDiagram();
      fitDiagram();
      return;
    }

    tasks.forEach((task, index) => {
      const lane = taskLanes.get(task.id || task.name);
      const shape = existingActivity(task) || createShape("bpmn:Task", task.name || String(task), {
        x: (anchor?.x || 180) + (anchor?.width || 36) + 125,
        y: lane.y + lane.height / 2,
      }, lane);
      connect(anchor, shape);
      anchor = shape;
      if (end && index === tasks.length - 1) moveEndAfter(end, anchor);
    });
    tidyDiagram();
    fitDiagram();
  }

  export function addGateway(type = "bpmn:ExclusiveGateway", name = "") {
    if (!modeler || !processStarted) return;
    const { modeling } = services();
    const { anchor, end } = insertionContext();
    if (!anchor) return;
    const gateway = createShape(type, "", {
      x: anchor.x + anchor.width + 125,
      y: anchor.y + anchor.height / 2,
    }, anchor.parent);
    if (name) modeling.updateProperties(gateway, { name });
    connect(anchor, gateway);
    if (end) {
      moveEndAfter(end, gateway);
    }
    tidyDiagram();
    fitDiagram();
  }

  export function finishProcess() {
    if (!modeler || !processStarted) return;
    const { elementRegistry } = services();
    if (elementRegistry.filter((element) => element.type === "bpmn:EndEvent").length) return;
    const { anchor } = insertionContext();
    if (!anchor) return;
    const end = createShape("bpmn:EndEvent", "End", {
      x: anchor.x + anchor.width + 150,
      y: anchor.y + anchor.height / 2,
    }, anchor.parent);
    connect(anchor, end);
    tidyDiagram();
    fitDiagram();
  }

  export function applyTaskInformation(taskInformation = [], options = {}) {
    if (!modeler || !processStarted) return;
    const { canvas, elementRegistry, modeling } = services();
    if (options.addSubprocess) {
      const bpmnReplace = modeler.get("bpmnReplace");
      elementRegistry.filter((element) => element.type === "bpmn:Task")
        .forEach((task) => bpmnReplace.replaceElement(task, { type: "bpmn:SubProcess", isExpanded: false }));
    }
    const participant = elementRegistry.filter((element) => element.type === "bpmn:Participant")[0];
    const artifactParent = participant || canvas.getRootElement();
    managedInformationIds.map((id) => elementRegistry.get(id)).filter(Boolean)
      .forEach((element) => modeling.removeShape(element));
    managedInformationIds = [];
    const artifacts = new Map();

    taskInformation.forEach((information) => {
      const task = elementRegistry.filter((element) =>
        ["bpmn:Task", "bpmn:SubProcess"].includes(element.type) && element.businessObject?.name === information.task
      )[0];
      if (!task) return;
      if (information.note) {
        const annotation = createShape("bpmn:TextAnnotation", "", { x: task.x + task.width + 55, y: task.y + task.height / 2 }, task.parent);
        modeling.updateProperties(annotation, { text: information.note });
        placeClear(annotation);
        modeling.connect(task, annotation, { type: "bpmn:Association" });
        managedInformationIds.push(annotation.id);
      }
      [
        ...(information.databases || []).map((name) => ({ name, type: "bpmn:DataStoreReference" })),
        ...(information.files || []).map((name) => ({ name, type: "bpmn:DataObjectReference" })),
      ].forEach(({ name, type }) => {
        const normalizedName = String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
        if (!normalizedName) return;
        const key = `${type}:${normalizedName}`;
        if (!artifacts.has(key)) artifacts.set(key, { name: String(name).trim(), type, tasks: new Set() });
        artifacts.get(key).tasks.add(task);
      });
    });

    const taskElements = flowNodes().filter((element) => ["bpmn:Task", "bpmn:SubProcess"].includes(element.type));
    const left = Math.min(...taskElements.map((element) => element.x), 220);
    const top = Math.min(...taskElements.map((element) => element.y), 250);
    [...artifacts.values()].forEach((entry, index) => {
      const artifact = createShape(entry.type, entry.name, {
        x: left + 80 + (index % 5) * 150,
        y: Math.max((participant?.y || 20) + 55, top - 145) + Math.floor(index / 5) * 75,
      }, artifactParent);
      placeClear(artifact);
      entry.tasks.forEach((task) => modeling.connect(artifact, task, { type: "bpmn:Association", associationDirection: "One" }));
      managedInformationIds.push(artifact.id);
    });
    tidyDiagram();
    fitDiagram();
  }

  export function applyThroughputTimes(transitions = []) {
    if (!modeler || !processStarted) return;
    const { canvas, elementRegistry, modeling } = services();
    const bpmnFactory = modeler.get("bpmnFactory");
    // Vorhandene Zeitgeber zuerst herausnehmen und die Verbindung wieder
    // schliessen. Ohne das reiht sich bei jedem "Apply and complete" eine
    // weitere Timer-Schicht hintereinander, bis der Auto-Layouter aufgibt
    // ("All bpmn-auto-layout strategies failed", im dritten Lauf beobachtet).
    // Erkannt werden sie an ihrer Zeitdefinition, nicht an gemerkten Ids: das
    // Modell wird beim Anwenden neu geladen, danach sind alte Ids wertlos.
    const vorhandeneTimer = elementRegistry.filter((element) => {
      if (element.type !== "bpmn:IntermediateCatchEvent") return false;
      const business = element.businessObject;
      const hasTimer = (business?.eventDefinitions || [])
        .some((definition) => definition.$type === "bpmn:TimerEventDefinition");
      // Nach dem Backend-Layout kommt das Modell neu herein; dabei geht die
      // Zeitdefinition im businessObject verloren. Die Beschriftung im Muster
      // "3 days & 7 hrs" weist die Zeitgeber dann trotzdem eindeutig aus.
      return hasTimer || /^\d+ days & \d+ hrs$/.test(String(business?.name || "").trim());
    });
    vorhandeneTimer.forEach((timer) => {
      const incoming = (timer.incoming || [])[0];
      const outgoing = (timer.outgoing || [])[0];
      const source = incoming?.source;
      const target = outgoing?.target;
      const name = incoming?.businessObject?.name || "";
      // Die beiden Kanten ausdruecklich mit entfernen: removeShape laesst sie
      // sonst stehen, die Einfuegeschleife sieht danach doppelt so viele
      // Strecken und setzt eine zweite Timer-Reihe (gemessen: 3 -> 6).
      [...(timer.incoming || []), ...(timer.outgoing || [])]
        .forEach((connection) => modeling.removeConnection(connection));
      modeling.removeShape(timer);
      if (!source || !target) return;
      const restored = modeling.connect(source, target, { type: "bpmn:SequenceFlow" });
      if (name) modeling.updateProperties(restored, { name });
    });

    const values = new Map(transitions.map((transition) => [
      `${transition.from}\u0000${transition.to}`,
      Number(transition.averageDurationSeconds),
    ]));
    function endpointNames(element, direction, visited = new Set()) {
      if (!element || visited.has(element.id)) return [];
      visited.add(element.id);
      if (!["bpmn:ExclusiveGateway", "bpmn:ParallelGateway", "bpmn:InclusiveGateway"].includes(element.type)) {
        return element.businessObject?.name ? [element.businessObject.name] : [];
      }
      const connections = direction === "incoming" ? element.incoming : element.outgoing;
      return [...new Set((connections || []).flatMap((connection) => endpointNames(
        direction === "incoming" ? connection.source : connection.target,
        direction,
        visited,
      )))];
    }

    elementRegistry.filter((element) => element.type === "bpmn:SequenceFlow").forEach((flow) => {
      const fromNames = endpointNames(flow.source, "incoming");
      const toNames = endpointNames(flow.target, "outgoing");
      const matches = fromNames.flatMap((from) => toNames.map((to) => values.get(`${from}\u0000${to}`)))
        .filter((value) => Number.isFinite(value) && value > 0);
      if (matches.length !== 1) return;
      const seconds = matches[0];
      const totalHours = Math.max(1, Math.floor(seconds / 3600));
      const label = `${Math.floor(totalHours / 24)} days & ${totalHours % 24} hrs`;
      const participant = elementRegistry.filter((element) => element.type === "bpmn:Participant")[0];
      const parent = flow.source.parent === flow.target.parent
        ? flow.source.parent
        : (participant || canvas.getRootElement());
      const source = flow.source;
      const target = flow.target;
      const flowName = flow.businessObject?.name || "";
      const timer = createShape("bpmn:IntermediateCatchEvent", label, {
        x: (source.x + source.width + target.x) / 2,
        y: (source.y + source.height / 2 + target.y + target.height / 2) / 2,
      }, parent);
      modeling.updateProperties(timer, {
        eventDefinitions: [bpmnFactory.create("bpmn:TimerEventDefinition")],
      });
      modeling.removeConnection(flow);
      const incoming = modeling.connect(source, timer, { type: "bpmn:SequenceFlow" });
      if (flowName) modeling.updateProperties(incoming, { name: flowName });
      modeling.connect(timer, target, { type: "bpmn:SequenceFlow" });
    });
    tidyDiagram();
    fitDiagram();
  }

  function emitState() {
    if (!modeler || importing) return;
    const elements = flowNodes();
    if (elements.length) processStarted = true;
    dispatch("change", {
      started: processStarted,
      activities: elements.filter((element) => ["bpmn:Task", "bpmn:SubProcess"].includes(element.type)).map((element) => element.businessObject?.name || "Activity"),
      gateways: elements.filter((element) => element.type?.endsWith("Gateway")).length,
      hasEnd: elements.some((element) => element.type === "bpmn:EndEvent"),
    });
  }

  onMount(() => {
    modeler = new Modeler({
      container: viewdiv,
      keyboard: { bindTo: document },
    });
    modeler.get("eventBus").on("commandStack.changed", emitState);
    if (bpmnXml) loadXml(bpmnXml);
    else loadXml(emptyDiagram());
    resizeObserver = new ResizeObserver(fitDiagram);
    resizeObserver.observe(viewdiv);
  });

  onDestroy(() => {
    resizeObserver?.disconnect();
    modeler?.destroy();
  });
</script>

<div class="inline-modeler">
  {#if loading}
    <div class="modeler_overlay">
      <Loading withOverlay={false} small />
      {#if loadingText}<span class="overlay_text">{loadingText}</span>{/if}
    </div>
  {/if}
  {#if error || importError}
    <div class="modeler_message error">{error || importError}</div>
  {/if}
  <div class="modeler_canvas" bind:this={viewdiv} />
</div>

<style>
  .inline-modeler {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: min(72vh, 52rem);
    overflow: hidden;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: var(--pax-white, #fff);
  }

  .overlay_text {
    color: var(--pax-black, #1d1d1f);
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .modeler_canvas {
    width: 100%;
    height: 100%;
    min-height: min(72vh, 52rem);
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
  }

  :global(.inline-modeler .djs-palette) {
    top: 1rem !important;
  }

  :global(.inline-modeler .djs-palette.two-column.open) {
    width: 40px !important;
  }

  :global(.inline-modeler .djs-palette.two-column .entry),
  :global(.inline-modeler .djs-palette .djs-palette-toggle) {
    float: none !important;
    width: 38px !important;
    height: 38px !important;
    font-size: 25px !important;
    line-height: 38px !important;
  }

  :global(.inline-modeler .bjs-powered-by) {
    display: none !important;
  }

  .modeler_overlay,
  .modeler_message {
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
    color: var(--pax-black, #1d1d1f);
    font-size: 1rem;
    font-weight: 600;
  }

  .modeler_message.error {
    padding: 2rem;
    color: #b42318;
    text-align: center;
  }
</style>
